import { flatten } from 'flat';
import update from 'immutability-helper';
import FormatService from './format-service';

export default class StructureService {
  private static FLATTEN_MAX_DEPTH_DEFAULT: number = 1;

  public static getItemMap(
    itemList: StructureItem[],
    itemKey: string,
    itemValueKey?: string,
  ): Record<string, StructureItem> {
    return itemList ? itemList.reduce((
      resultMap: Record<string, StructureItem>, item: StructureItem,
    ): Record<string, StructureItem> => ({
      ...resultMap,
      [item[itemKey]]: itemValueKey ? item[itemValueKey] : item,
    }), {}) : {};
  }

  public static getNewItemId(newItemList?: StructureItem[], itemList?: StructureItem[], groupKey?: string): number {
    const usePositive: boolean = !!itemList;
    const mathFunction: (...values: number[]) => number = usePositive ? Math.max : Math.min;

    let newItemId: number = 0;
    if (newItemList && newItemList.length) {
      newItemId = mathFunction(...newItemList.map((item: StructureItem): number => item.id));
    } else if (itemList && itemList.length) {
      newItemId = mathFunction(...itemList.map((item: StructureItem): number => {
        if (groupKey) {
          if (item[groupKey] && item[groupKey].length) {
            const groupItemIds: number[] = item[groupKey].map((groupItem: StructureItem): number => groupItem.id);

            return mathFunction(...groupItemIds);
          }

          return 0;
        }

        return item.id;
      }));
    }

    return usePositive ? newItemId + 1 : newItemId - 1;
  }

  public static extendOptions(
    defaultOptions: StructureItem[],
    dynamicOptions: StructureItem[] | null,
    isSorted?: boolean,
  ): StructureItem[] {
    const defaultOptionMap: Record<string, StructureItem> = defaultOptions.reduce((
      optionMap: Record<string, StructureItem>, option: StructureItem,
    ): Record<string, StructureItem> => ({
      ...optionMap,
      [option.name]: option,
    }), {});

    const extendedOptions: StructureItem[] = dynamicOptions && dynamicOptions.length
      ? dynamicOptions.filter((option: StructureItem): boolean => !!option)
        .map((option: StructureItem): StructureItem => ({
          ...defaultOptionMap[option.name],
          ...option,
        })).filter((option: StructureItem): boolean => option && !!option.id) : [];

    const staticOptions: StructureItem[] = Object.keys(defaultOptionMap)
      .map((optionName: string): StructureItem | null => (
        !extendedOptions.find((option: StructureItem): boolean => option.name === optionName)
          ? defaultOptionMap[optionName] : null
      )).filter((option: StructureItem | null): boolean => !!option) as StructureItem[];

    const resultExtendedOptions: StructureItem[] = [...extendedOptions, ...staticOptions];

    return isSorted ? resultExtendedOptions.sort((itemA: StructureItem, itemB: StructureItem): number => (
      itemA.id - itemB.id
    )) : resultExtendedOptions;
  }

  public static switchListItems(list: StructureItem[], fromIndex: number, toIndex: number): StructureItem[] {
    return update(list, {
      $splice: [
        [fromIndex, 1],
        [toIndex, 0, list[fromIndex]],
      ],
    });
  }

  public static flattenObject(object: StructureItem, maxDepth?: number): StructureItem {
    return flatten({
      ...object,
    }, {
      maxDepth: maxDepth || this.FLATTEN_MAX_DEPTH_DEFAULT,
    });
  }

  public static selectRow(
    selectedRows: StructureItem[] | Record<string, StructureItem>, rowId: number, rowGroupId?: number,
  ): StructureItem[] {
    let newSelectedRows: StructureItem[] = [];

    const resultSelectedRows: StructureItem[] = rowGroupId || rowGroupId === 0
      ? (selectedRows as Record<string, StructureItem>)[rowGroupId.toString()] : selectedRows as StructureItem[];

    const selectedIndex: number = resultSelectedRows.indexOf(rowId);
    if (selectedIndex === FormatService.INDEX_NOT_FOUND) {
      newSelectedRows = [...newSelectedRows, ...resultSelectedRows, rowId];
    } else if (selectedIndex === 0) {
      newSelectedRows = [...newSelectedRows, ...resultSelectedRows.slice(1)];
    } else if (selectedIndex === resultSelectedRows.length - 1) {
      newSelectedRows = [...newSelectedRows, ...resultSelectedRows.slice(0, -1)];
    } else if (selectedIndex > 0) {
      newSelectedRows = [
        ...newSelectedRows,
        ...(resultSelectedRows.slice(0, selectedIndex)),
        ...(resultSelectedRows.slice(selectedIndex + 1)),
      ];
    }

    return newSelectedRows;
  }

  public static getListItemIndexById(list: StructureEntity[], id: number): number {
    return list.findIndex((listItem: StructureEntity): boolean => listItem.id === id);
  }

  public static deleteListItemById(list: StructureEntity[], id: number): StructureEntity[] {
    const listItemIndex: number = this.getListItemIndexById(list, id);

    return this.deleteListItemByIndex(list, listItemIndex);
  }

  public static deleteListItemByIndex(list: StructureItem[], listItemIndex: number): StructureItem[] {
    if (listItemIndex || listItemIndex === 0) {
      list.splice(listItemIndex, 1);
    }

    return list;
  }

  public static insertListItemByIndex(
    list: StructureItem[], listItemIndex: number, newListItem: StructureItem,
  ): StructureItem[] {
    if (listItemIndex || listItemIndex === 0) {
      list.splice(listItemIndex, 0, newListItem);
    }

    return list;
  }

  public static resetObjectListIds(list: StructureItem[]): StructureItem[] {
    return list.map((listItem: StructureItem, listItemIndex: number): StructureItem => ({
      id: listItemIndex + 1,
      ...listItem,
    }));
  }

  public static calculateDiff(originalData: JsonObject | null, resultData: JsonObject | null): JsonDiff | null {
    return this.processCalculateDiff(originalData || {}, resultData || {}, {});
  }

  public static processCalculateDiff(
    originalData: JsonObject | null, resultData: JsonObject | null, diff: JsonDiff | null,
  ): JsonDiff | null {
    if (!originalData && resultData) {
      return {
        result: resultData,
      };
    }

    if (!resultData && originalData) {
      return {
        original: originalData,
      };
    }

    for (let prop in originalData) {
      if (!this.checkIsValueObject(originalData[prop]) || originalData[prop] == null) {
        const originalValue: StructureItem | null = (originalData && originalData[prop]) || null;
        const resultValue: StructureItem | null = (resultData && resultData[prop]) || null;

        if (originalValue !== resultValue) {
          diff[prop] = {
            original: originalValue,
            result: resultValue,
          };
        }
      } else if (this.checkIsValueArray(originalData[prop])) {
        diff[prop] = [];

        for (let index: number = 0; index < originalData[prop].length; index++) {
          diff[prop][index] = this.processCalculateDiff(
            originalData[prop][index], resultData[prop][index], diff[prop][index] || {},
          );

          if (this.checkIsValueObject(diff[prop][index]) && !Object.keys(diff[prop][index]).length) {
            delete diff[prop][index];
          }
        }

        diff[prop] = diff[prop].filter((diffItem: StructureItem): boolean => !!diffItem);

        if (!diff[prop].length) {
          delete diff[prop];
        }
      } else {
        diff[prop] = this.processCalculateDiff(originalData[prop] || null, resultData[prop] || null, {});

        if (!Object.keys(diff[prop]).length) {
          delete diff[prop];
        }
      }

      if (!diff[prop]) {
        delete diff[prop];
      }
    }

    return diff;
  }

  public static checkIfArraysEqual(arrayOne?: StructureItem[], arrayTwo?: StructureItem[]): boolean {
    if ((!arrayOne && !arrayTwo) || (arrayOne && !arrayOne.length && arrayTwo && !arrayTwo.length)) {
      return true;
    }

    if (!arrayOne || !arrayOne.length || !arrayTwo || !arrayTwo.length) {
      return false;
    }

    return arrayOne.length === arrayTwo.length && arrayOne.every((item: StructureItem, itemIndex: number): boolean => (
      item === arrayTwo[itemIndex]
    ));
  }

  public static checkPermission(permission: string, permissionList: string[]): boolean {
    return !!permission && permissionList && !!permissionList.length && permissionList.includes(permission);
  }

  public static checkIsValueObject(value: StructureItem): boolean {
    return typeof value === 'object';
  }

  public static checkIsValueArray(value: StructureItem): boolean {
    return Array.isArray(value);
  }

  public static emptyAction(): void {
    // Do nothing
  }
}
